import { PayableInbox } from "@/Billing/Components/PayableInbox";
import { EmptyProps } from "@/Common/Components/BaseReactComponent";
import { EnumMyleLoginPermission } from "@/Common/Models/EnumMyleLoginPermission";
import { BillingInbox } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { CountBillingClaimStatusRequest } from "../Api/PublicBilling/Dto/Request/CountBillingClaimStatusRequest";
import { getBillingClaimStatusCount } from "../Api/PublicBilling/Dto/Response/BillingClaimStatusClient";
import { BillingHelper } from "../Helpers/BillingHelper";
import { PayableInboxFilterSettings } from "../Models/Settings/PayableInboxFilterSettings";
import { BillingClaimStatusInboxModel } from "../Models/UI/BillingClaimStatusInboxModel";
import { IBillingTabModel } from "../Models/UI/IBillingTabModel";
import { ISalesInboxModel } from "../Models/UI/ISalesInboxModel";
import { ITransactionsInboxModel } from "../Models/UI/ITransactionsInboxModel";
import { SalesInbox } from "./PrivateBilling/SalesInbox";
import { TransactionsInbox } from "./PrivateBilling/TransactionsInbox";
import { BillingClaimStatusInbox } from "./PublicBilling/BillingClaimStatusInbox";
import { BillingTreeControl } from "./BillingTreeControl";

class BillingTabState {
    public Model: IBillingTabModel;
    public TransactionsInboxModel: ITransactionsInboxModel;
    public BillingClaimStatusInboxModel: BillingClaimStatusInboxModel;
    public SalesInboxModel: ISalesInboxModel;
    public payableBillsFilterSettings: PayableInboxFilterSettings;
}

class BillingTab extends React.Component<EmptyProps, BillingTabState> {
    private changeListenerMethodId: string;
    private countRef: any;

    constructor(props: EmptyProps) {
        super(props);

        this.countRef = React.createRef();

        const initialState = new BillingTabState();

        initialState.Model = billingStore.Model;
        initialState.TransactionsInboxModel = billingStore.TransactionsInboxModel;
        initialState.SalesInboxModel = billingStore.SalesInboxModel;
        initialState.BillingClaimStatusInboxModel = billingStore.BillingClaimStatusInboxModel;
        if (cacheStore.ClinicSettings.EnablePrivateBillRecipient)
            initialState.payableBillsFilterSettings = billingStore.payableInboxModel.filterSettings;

        this.state = initialState;
    }

    public render(): JSX.Element {
        return (
            <div className="sub-container">
                <div className="left-nav-menu transition">
                    <div className="ws-scrollable hide-overflow">
                        <BillingTreeControl
                            CurrentInbox={billingStore.Model.get("CurrentInbox")}
                            TransactionsInboxModel={this.state.TransactionsInboxModel}
                            SalesInboxModel={this.state.SalesInboxModel}
                            BillingModel={this.state.Model}
                            payableBillsFilterSettings={this.state.payableBillsFilterSettings}
                            ClaimStatusCount={billingStore.Model.get("BillingClaimStatusCount")}
                        />
                    </div>
                </div>
                {this.renderCurrentInbox()}
            </div>
        );
    }

    private renderCurrentInbox(): JSX.Element {
        switch (this.state.Model.get("CurrentInbox")) {
            case BillingInbox.Transactions:
                return <TransactionsInbox Model={this.state.TransactionsInboxModel} />;
            case BillingInbox.Sales:
                return <SalesInbox Model={this.state.SalesInboxModel} />;
            case BillingInbox.Payable:
                return <PayableInbox />;
            case BillingInbox.BillingClaimStatus:
                return <BillingClaimStatusInbox model={this.state.BillingClaimStatusInboxModel} onActionCallback={() => this.getBillingClaimStatusCountResponse()} />;
        }
    }

    public shouldComponentUpdate(nextProps: EmptyProps, nextState: BillingTabState): boolean {
        return (
            this.state.Model !== nextState.Model
            || this.state.TransactionsInboxModel !== nextState.TransactionsInboxModel
            || this.state.SalesInboxModel !== nextState.SalesInboxModel
            || this.state.payableBillsFilterSettings !== nextState.payableBillsFilterSettings
            || this.state.BillingClaimStatusInboxModel !== nextState.BillingClaimStatusInboxModel
        );
    }

    public componentDidMount(): void {
        if (cacheStore.ClinicSettings.EnableBillingReconciliationReport
            && BillingHelper.isPublicBillPermission()) {
            this.getPublicBillingClaimStatusDebounce();
        }

        this.changeListenerMethodId = billingStore.AddChangeListener(this.changeListener, this, "billing-tab-listener");
        sessionStore.dispatch(LocalOperations.Billing.Store_Mounted);
    }

    public componentWillUnmount(): void {
        billingStore.RemoveChangeListener(this.changeListenerMethodId);
    }

    public changeListener(): void {
        const state = new BillingTabState();

        state.Model = billingStore.Model;
        state.TransactionsInboxModel = billingStore.TransactionsInboxModel;
        state.SalesInboxModel = billingStore.SalesInboxModel;
        state.BillingClaimStatusInboxModel = billingStore.BillingClaimStatusInboxModel;
        if (cacheStore.ClinicSettings.EnablePrivateBillRecipient)
            state.payableBillsFilterSettings = billingStore.payableInboxModel.filterSettings;

        this.setState(state);
    }

    private getPublicBillingClaimStatusDebounce(): void {
        if (!this.countRef.current) {
            this.countRef.current = setTimeout(() => { this.countRef.current = null; }, 2500);
            this.getBillingClaimStatusCountResponse();
            return;
        }

        clearTimeout(this.countRef.current);
        this.countRef.current = setTimeout(() => this.getBillingClaimStatusCountResponse(), 2500);
    }

    private async getBillingClaimStatusCountResponse(): Promise<void> {
        let providerIds: string[] = [];
        if (sessionStore.hasPermission(EnumMyleLoginPermission.BillingTabPermission))
            providerIds = cacheStore.EmployeeCacheWithInactive.map(item => item.ContactFacet_id);
        else
            providerIds = [sessionStore.getCurrentEmployee().ContactFacetId];

        const employees = cacheStore.EmployeeCacheWithInactive.filter(item => providerIds.contains(item.ContactFacet_id));
        let siteIds: string[] = [];
        employees.forEach(e => e.AssociatedSites.forEach(a => siteIds.push(a)));
        const request: CountBillingClaimStatusRequest = { SiteIds: siteIds.distinct(), ProviderIds: providerIds.distinct() };
        const countResponse = await getBillingClaimStatusCount(request);
        this.countRef.current = null;
        sessionStore.dispatch(LocalOperations.Billing.BillingClaimStatusCount, { claimcount: countResponse.StatusCount });
    }
}

export { BillingTab };
